import { RouteProps } from '../../../routes/AppRouter'
import { Product } from '../../../modules/product/models/Product'
import { useEffect, useState } from 'react'
import { useStyles } from '../Application.styles'
import { COLOR_PRIMARY } from '../../../routes/color-constants'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { FormType } from '../../../common/enums/Enums'
import { getFormContainer } from 'container/form-module'
import { FormService } from 'modules/Form/services/FormService'
import { FORM_SERVICE_KEY } from 'modules/Form'
import { emptyNewProductDTO, FormRow, NewProductDTO } from 'modules/Form/models/Form'
import { isValid } from 'date-fns'
import { convertTimeToLocal, formatDate } from '../../../common/utils/DataFormatters'
import { v4 as uuidv4 } from 'uuid'
import { getPatientContainer } from 'container/patient-modules'
import { PatientService } from 'modules/patient/services/PatientService'
import { PATIENT_SERVICE_KEY } from 'modules/patient'
import { getClientContainer } from 'container/client-modules'
import { ClientService } from 'modules/clients/services/ClientService'
import { CLIENTS_SERVICE_KEY } from 'modules/clients'
import { Query, QueryParam } from 'common/api/Query'
import { AllPatients } from 'modules/patient/models/patientForm/AllPatients'
import {
  PatientCustom,
  PatientDataField,
} from 'modules/patient/models/patientForm/PatientCustom'
import { SampleCustom } from 'modules/sampleType/models/sampleForm/SampleCustom'
import { emptySampleCustomDTO } from 'modules/sampleType/models/sampleForm/SampleCustomDTO'

export type FormCustomProps = RouteProps & {
  patients: PatientCustom[]
  productIndex: number
  handleChangePatients: (productIndex: number, patients: any[], type: FormType) => void
  product: Product
  type?: string
  edit?: boolean
  clientIDDoingTheRequest: string
  setIsOpen: (bool: boolean) => void
  setCurrentProduct: (curentProduct: number) => void
  setCurentType: (curentType: number) => void
}

const formService = getFormContainer().get<FormService>(FORM_SERVICE_KEY)
const patientService = getPatientContainer().get<PatientService>(PATIENT_SERVICE_KEY)
const clientService = getClientContainer().get<ClientService>(CLIENTS_SERVICE_KEY)

/** Campos del modelo estándar de paciente que se pueden pre-rellenar automáticamente */
const STANDARD_FIELD_MAP: Record<string, (p: AllPatients & Record<string, any>) => any> = {
  firstName:    (p) => p.firstName    ?? '',
  lastName:     (p) => p.lastName     ?? '',
  dob:          (p) => p.dob          ?? '',
  nhc:          (p) => p.nhc          ?? '',
  nss:          (p) => p.nss          ?? '',
  phone:        (p) => p.phone        ?? '',
  email:        (p) => p.email        ?? '',
  sex:          (p) => p.sex          ?? '',
  province:     (p) => p.province     ?? '',
  zipCode:      (p) => p.zipCode      ?? '',
  indication:   (p) => p.indication   ?? '',
  clinicHistory:(p) => p.clinicHistory ?? '',
  nhcOrDni:     (p) => p.nhcOrDni ?? p.nhc ?? '',
}

function emptyCustomPatient(rows: FormRow[], odooMap: Record<string, string>): PatientCustom {
  const data: PatientDataField[] = rows.flatMap(row =>
    row.items.map(item => ({
      key: item.field.name,
      odooField: odooMap[item.field.name] ?? item.field.odooField ?? '',
      value: item.field.type === 'checkbox' ? false : item.field.type === 'checkboxGroup' ? [] : '',
    }))
  )
  return new PatientCustom({
    _id: uuidv4(),
    _firstName: '',
    _lastName: '',
    _nhc: '',
    _clinicHistory: '',
    _patientData: data,
    _sample: new SampleCustom(emptySampleCustomDTO()),
  })
}

function resolveFieldValue(
  previous: AllPatients & Record<string, any>,
  key: string,
  odooField: string,
  fieldType: string,
): any {
  if (STANDARD_FIELD_MAP[key]) {
    return STANDARD_FIELD_MAP[key](previous)
  }
  if (previous[key] !== undefined && previous[key] !== null) {
    return previous[key]
  }
  if (odooField && previous[odooField] !== undefined && previous[odooField] !== null) {
    return previous[odooField]
  }
  return fieldType === 'checkbox' ? false : fieldType === 'checkboxGroup' ? [] : ''
}

function patientToCustom(
  previous: AllPatients & Record<string, any>,
  rows: FormRow[],
  odooMap: Record<string, string>,
): PatientCustom {
  const data: PatientDataField[] = rows.flatMap(row =>
    row.items.map(item => {
      const key = item.field.name
      const odooField = odooMap[key] ?? item.field.odooField ?? ''
      const value = resolveFieldValue(previous, key, odooField, item.field.type)
      return { key, odooField, value }
    })
  )
  return new PatientCustom({
    _id: previous.id ?? uuidv4(),
    _firstName: previous.firstName ?? '',
    _lastName: previous.lastName ?? '',
    _nhc: previous.nhc ?? '',
    _clinicHistory: previous.clinicHistory ?? '',
    _patientData: data,
    _sample: new SampleCustom(emptySampleCustomDTO()),
  })
}

function filtrarPersonas(personas: any[]): any[] {
  const map: Record<string, any> = {}
  personas.forEach((p) => {
    const key = p.firstName + p.lastName
    if (map[key]) {
      if (p.priority) map[key] = p
    } else {
      map[key] = p
    }
  })
  return Object.values(map)
}

export const PatientFormCustom = (props: FormCustomProps) => {
   const { classes } = useStyles({ color: COLOR_PRIMARY })
   const { t, i18n } = useTranslation()

   const [formDef, setFormDef] = useState<NewProductDTO | null>(null)
   const [formError, setFormError] = useState(false)
   const [patients, setPatients] = useState<PatientCustom[]>(props.patients || [])
   const [loading, setLoading] = useState(true)
   const [previousPatients, setPreviousPatients] = useState<(AllPatients & Record<string, any>)[]>([])
   const [selectedPatientID, setSelectedPatientID] = useState<string[]>([])
   const [odooFieldsMap, setOdooFieldsMap] = useState<Record<string, string>>({})

   const patientNumber = props.product?.patientNumber ?? 1

   useEffect(() => {
     let cancelled = false
     const typeForm = props.product?.typeForm
     if (!typeForm) { setLoading(false); setFormError(true); return }
      const sub = formService.byTypeForm(typeForm).subscribe((res) => {
       if (cancelled) return
       if (res) {
          const def: NewProductDTO = {
            id: res.id,
            productName: res.productName,
            odooNumber: res.odooNumber,
            patientForm: res.patientForm,
            sampleForm: res.sampleForm,
          }
         setFormDef(def)
         setFormError(false)
         const map: Record<string, string> = {}
         def.patientForm.forEach((row) => {
           row.items.forEach((item) => {
             if (item.field.odooField) {
               map[item.field.name] = item.field.odooField
             }
           })
         })
         setOdooFieldsMap(map)
       } else {
         setFormError(true)
       }
       setLoading(false)
     })
     return () => { cancelled = true; sub.unsubscribe() }
   }, [props.product?.typeForm])

   useEffect(() => {
     clientService.getByID(props.clientIDDoingTheRequest).subscribe(async (res) => {
       if (!res) return
       const all = await patientService
         .getAllPatients(new Query({ query: [new QueryParam('clientID', res.id || '')] }))
         .toPromise()
       const list = filtrarPersonas([...(all?.items ?? [])])
       setPreviousPatients(list)
     })
   }, [props.clientIDDoingTheRequest])

   useEffect(() => {
     if (props.patients?.length > 0) {
       setSelectedPatientID(props.patients.map((p) => p.id ?? ''))
     } else {
       setSelectedPatientID(Array(patientNumber).fill(''))
     }
   }, [])

   useEffect(() => {
     if (previousPatients.length === 0 || !props.patients?.length) return
     const aux: string[] = [...selectedPatientID]
     props.patients.forEach((p, i) => {
       const found = previousPatients.find((prev) => prev.id === p.id)
       if (found) aux[i] = found.id ?? ''
     })
     setSelectedPatientID(aux)
   }, [previousPatients])

   useEffect(() => {
     if (loading || !formDef) return
     if (
       props.type !== 'view' &&
       ((props.patients !== null && props.patients?.length === 0) || props.patients == null)
     ) {
       const empty = Array.from({ length: patientNumber }, () =>
         emptyCustomPatient(formDef.patientForm, odooFieldsMap),
       )
       propagate(empty)
     }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loading, formDef])

   useEffect(() => {
     if (!props.patients) return
     // Garantizar que todos los elementos sean instancias de PatientCustom con sus
     // métodos getFieldValue / setFieldValue disponibles, independientemente de si
     // vienen del wizard (objetos planos deserializados) o del estado del padre.
     const ensured = props.patients.map((p: any) => {
       if (typeof p?.getFieldValue === 'function') return p as PatientCustom
       // Objeto plano: reconstruir como instancia
       const rawSample = p?._sample ?? {}
       const sample = new SampleCustom({
         _id: rawSample._id ?? undefined,
         _typeSample: rawSample._typeSample ?? '',
         _sampleData: Array.isArray(rawSample._sampleData) ? rawSample._sampleData : [],
       })
       return new PatientCustom({
         _id: p?._id ?? undefined,
         _firstName: p?._firstName ?? '',
         _lastName: p?._lastName ?? '',
         _nhc: p?._nhc ?? '',
         _clinicHistory: p?._clinicHistory ?? '',
         _patientData: Array.isArray(p?._patientData)
           ? p._patientData.map((f: any) => ({
               key: f.key ?? '',
               odooField: f.odooField ?? '',
               value: f.value ?? '',
             }))
           : [],
         _sample: sample,
       })
     })
     setPatients(ensured)
   }, [props.patients])

   // En modo edición, cuando los datos del wizard se cargan con pacientes existentes
   // necesitamos propagar los datos al padre para que estén disponibles al guardar.
   useEffect(() => {
     if (!loading && formDef && props.patients?.length && props.type === 'edit') {
       props.handleChangePatients(props.productIndex, props.patients as any, formDef.odooNumber)
     }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loading, formDef])

   useEffect(() => {
     if (props.type === 'view' && props.patients && formDef) {
       props.handleChangePatients(props.productIndex, props.patients as any, formDef.odooNumber)
     }
   }, [props.type])

   const propagate = (updatedPatients: PatientCustom[]) => {
     if (!formDef) return
     props.handleChangePatients(props.productIndex, updatedPatients as any, formDef.odooNumber)
   }

   const handleSetSelectedValue = async (patientIndex: number, id: string) => {
     if (!formDef) return
     const newPatients = [...patients]
     if (id) {
       const found = previousPatients.find((p) => p.id === id)
       let patientData: Record<string, any> = {}
       if (found) {
         patientData = { ...(found as any) }
       }
       try {
         const fullPatient = await patientService.getByID(id).toPromise()
         if (fullPatient) {
           patientData.id = id
           if (fullPatient.firstName) patientData.firstName = fullPatient.firstName
           if (fullPatient.lastName) patientData.lastName = fullPatient.lastName
           if (fullPatient.indication) patientData.indication = fullPatient.indication
         }
       } catch {
         // fall back to basic list data
       }
       newPatients[patientIndex] = Object.keys(patientData).length > 0
         ? patientToCustom(patientData as any, formDef.patientForm, odooFieldsMap)
         : emptyCustomPatient(formDef.patientForm, odooFieldsMap)
     } else {
       newPatients[patientIndex] = emptyCustomPatient(formDef.patientForm, odooFieldsMap)
     }
     setPatients(newPatients)
     propagate(newPatients)
     const auxSelected = [...selectedPatientID]
     auxSelected[patientIndex] = id
     setSelectedPatientID(auxSelected)
   }

   const handleFieldChange = (patientIndex: number, fieldName: string, value: any) => {
     if (!formDef) return
     const updated = [...patients]
     const original = updated[patientIndex] || emptyCustomPatient(formDef.patientForm, odooFieldsMap)
     // Clonar el PatientCustom para que cada paciente tenga su propio estado independiente
     const p = new PatientCustom({
       _id: original._id,
       _firstName: original._firstName,
       _lastName: original._lastName,
       _nhc: original._nhc,
       _clinicHistory: original._clinicHistory,
       _patientData: original._patientData.map(f => ({ ...f })),
       _sample: original._sample,
     })
     p.setFieldValue(fieldName, value, odooFieldsMap[fieldName])
     if (fieldName === 'firstName') p.firstName = String(value ?? '')
     if (fieldName === 'lastName') p.lastName = String(value ?? '')
     if (fieldName === 'nhc') p.nhc = String(value ?? '')
     if (fieldName === 'clinicHistory') p.clinicHistory = String(value ?? '')
     updated[patientIndex] = p
     setPatients(updated)
     propagate(updated)
   }

   const handleCheckboxGroupChange = (
     patientIndex: number,
     fieldName: string,
     optionValue: string,
     checked: boolean,
   ) => {
     const current: string[] = patients[patientIndex]?.getFieldValue(fieldName) ?? []
     const next = checked
       ? [...current, optionValue]
       : current.filter((v) => v !== optionValue)
     handleFieldChange(patientIndex, fieldName, next)
   }

   const openCopyModal = () => {
     if (!formDef) return
     props.setCurrentProduct(props.productIndex)
     props.setCurentType(formDef.odooNumber)
     props.setIsOpen(true)
   }

   const renderField = (
     patientIndex: number,
     fieldName: string,
     field: FormRow['items'][0]['field'],
     width: number,
     disabled: boolean,
   ) => {
     const value = patients[patientIndex]?.getFieldValue(fieldName) ?? ''

     switch (field.type) {
       case 'text':
       case 'number':
       case 'email':
         return (
           <Grid item xs={12} md={width} key={fieldName}>
             <TextField
               fullWidth
               variant="filled"
               label={t(field.title) || field.title}
               type={field.type}
               disabled={disabled}
               required={field.isRequired}
               value={value}
               onChange={(e) => handleFieldChange(patientIndex, fieldName, e.target.value)}
             />
           </Grid>
         )

       case 'textarea':
         return (
           <Grid item xs={12} md={width} key={fieldName}>
             <TextField
               fullWidth
               multiline
               rows={4}
               variant="outlined"
               label={t(field.title) || field.title}
               disabled={disabled}
               required={field.isRequired}
               value={value}
               onChange={(e) => handleFieldChange(patientIndex, fieldName, e.target.value)}
               InputLabelProps={{ shrink: true }}
             />
           </Grid>
         )
       case 'date':
         return (
           <Grid item xs={12} md={width} key={fieldName}>
             <LocalizationProvider dateAdapter={AdapterDateFns}>
               <DatePicker
                 disabled={disabled}
                 format="dd/MM/yyyy"
                 label={t(field.title) || field.title}
                 value={convertTimeToLocal(value) || null}
                 onChange={(date) =>
                   date && isValid(date) && handleFieldChange(patientIndex, fieldName, formatDate(date))
                 }
                 slotProps={{
                   textField: { size: 'small', fullWidth: true, required: field.isRequired },
                 }}
               />
             </LocalizationProvider>
           </Grid>
         )

       case 'select':
         return (
           <Grid item xs={12} md={width} key={fieldName}>
             <FormControl fullWidth variant="outlined">
               <InputLabel>
                 {(t(field.title) || field.title) + (field.isRequired ? ' *' : '')}
               </InputLabel>
               <Select
                 label={(t(field.title) || field.title) + (field.isRequired ? ' *' : '')}
                 value={value}
                 disabled={disabled}
                 required={field.isRequired}
                 onChange={(e) => handleFieldChange(patientIndex, fieldName, e.target.value)}
               >
                 <MenuItem value="">
                   <em>{t('notSelect')}</em>
                 </MenuItem>
                 {(field.options ?? []).map((opt) => (
                   <MenuItem key={opt.value} value={opt.value}>
                     {opt.text}
                   </MenuItem>
                 ))}
               </Select>
             </FormControl>
           </Grid>
         )

       case 'checkbox':
         return (
           <Grid item xs={12} md={width} key={fieldName}>
             <FormControlLabel
               control={
                 <Checkbox
                   checked={!!value}
                   disabled={disabled}
                   onChange={(_, checked) => handleFieldChange(patientIndex, fieldName, checked)}
                 />
               }
               label={t(field.title) || field.title}
             />
           </Grid>
         )

       case 'checkboxGroup': {
         const selected: string[] = Array.isArray(value) ? value : []
         return (
           <Grid item xs={12} md={width} key={fieldName}>
             <Typography variant="body2" gutterBottom>
               {t(field.title) || field.title}
               {field.isRequired && ' *'}
             </Typography>
             <FormGroup row>
               {(field.options ?? []).map((opt) => (
                 <FormControlLabel
                   key={opt.value}
                   control={
                     <Checkbox
                       size="small"
                       checked={selected.includes(opt.value)}
                       disabled={disabled}
                       onChange={(_, checked) =>
                         handleCheckboxGroupChange(patientIndex, fieldName, opt.value, checked)
                       }
                     />
                   }
                   label={opt.text}
                 />
               ))}
             </FormGroup>
           </Grid>
         )
       }

       default:
         return null
     }
   }

   if (loading) {
     return (
       <Typography color="text.secondary" sx={{ mt: 2 }}>
         {t('loading')}…
       </Typography>
     )
   }

   if (formError || !formDef) {
     return (
       <Typography color="error" sx={{ mt: 2 }}>
         {t('formNotFound') || 'No se encontró el formulario asociado al producto'}
       </Typography>
     )
   }

   if (!formDef.patientForm || formDef.patientForm.length === 0) {
     return (
       <Typography color="text.secondary" sx={{ mt: 2 }}>
         {t('noPatientFormDefined')}
       </Typography>
     )
   }

   const disabled = props.edit === false

   return (
     <Grid container direction="column">
       {[...Array(patientNumber)].map((_, patientIndex) => (
         <Grid container spacing={4} key={patientIndex} style={{ marginTop: '40px' }}>
           <Grid item xs={12} style={{ textAlign: 'left', fontWeight: 'bold', fontSize: '18px' }}>
             {t('fillPatientData') +
               ' ' + (patientIndex + 1) +
               ' ' + t('for') +
               ' ' + (i18n.language === 'es' ? props.product.nameES : props.product.nameEN)}
           </Grid>

           {props.edit !== false && (
             <Grid item xs={12}>
               <FormControl variant="outlined" fullWidth>
                 <InputLabel id={`selectPatientLabel-${patientIndex}`}>
                   {t('selectPatient')}
                 </InputLabel>
                 <Select
                   labelId={`selectPatientLabel-${patientIndex}`}
                   label={t('selectPatient')}
                   style={{ width: '50%' }}
                   value={selectedPatientID[patientIndex] ?? ''}
                   onChange={(e) => handleSetSelectedValue(patientIndex, e.target.value as string)}
                 >
                   {previousPatients.map((patient) => (
                     <MenuItem key={patient.id} value={patient.id}>
                       {patient.firstName + ' ' + patient.lastName}
                     </MenuItem>
                   ))}
                   <MenuItem value="">
                     {t('notSelect')}
                   </MenuItem>
                 </Select>

                 {props.productIndex > 0 && (
                   <Button
                     variant="contained"
                     onClick={openCopyModal}
                     style={{ display: 'flex', alignSelf: 'flex-end' }}
                   >
                     {t('aplyPatientes')}
                   </Button>
                 )}
               </FormControl>
             </Grid>
           )}

           {formDef.patientForm.map((row) =>
             row.items.map((item) =>
               renderField(patientIndex, item.field.name, item.field, item.width, disabled),
             ),
           )}
         </Grid>
       ))}
     </Grid>
   )
}
